import React, { useRef, useState, useEffect } from "react";

type JoystickProps = {
  onMove: (x: number, y: number) => void;
  onStop: () => void;
};

export default function Joystick({ onMove, onStop }: JoystickProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const stickRef = useRef<HTMLDivElement>(null);
  const [isActive, setIsActive] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const touchIdRef = useRef<number | null>(null);

  const JOYSTICK_SIZE = 120;
  const STICK_SIZE = 60;
  const MAX_DISTANCE = (JOYSTICK_SIZE - STICK_SIZE) / 2;

  const getPositionFromEvent = (e: TouchEvent | MouseEvent): { x: number; y: number } => {
    const container = containerRef.current;
    if (!container) return { x: 0, y: 0 };

    const rect = container.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    let clientX: number, clientY: number;
    if (e instanceof TouchEvent) {
      const touch = Array.from(e.touches).find(t => t.identifier === touchIdRef.current) || e.touches[0];
      clientX = touch.clientX;
      clientY = touch.clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: clientX - centerX,
      y: clientY - centerY,
    };
  };

  const updateStickPosition = (x: number, y: number) => {
    const distance = Math.sqrt(x * x + y * y);
    const angle = Math.atan2(y, x);

    if (distance > MAX_DISTANCE) {
      x = Math.cos(angle) * MAX_DISTANCE;
      y = Math.sin(angle) * MAX_DISTANCE;
    }

    setPosition({ x, y });

    // Normalize to -1 to 1 range
    const normalizedX = x / MAX_DISTANCE;
    const normalizedY = -y / MAX_DISTANCE; // Invert Y for intuitive control

    onMove(normalizedX, normalizedY);
  };

  const handleStart = (e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault();
    setIsActive(true);

    if (e.type.startsWith("touch")) {
      const touchEvent = e as React.TouchEvent;
      touchIdRef.current = touchEvent.touches[0].identifier;
    }

    const pos = getPositionFromEvent(e.nativeEvent as TouchEvent | MouseEvent);
    updateStickPosition(pos.x, pos.y);
  };

  const handleMove = (e: TouchEvent | MouseEvent) => {
    if (!isActive) return;

    const pos = getPositionFromEvent(e);
    updateStickPosition(pos.x, pos.y);
  };

  const handleEnd = () => {
    setIsActive(false);
    touchIdRef.current = null;
    setPosition({ x: 0, y: 0 });
    onStop();
  };

  useEffect(() => {
    if (!isActive) return;

    const handleMouseMove = (e: MouseEvent) => handleMove(e);
    const handleMouseUp = () => handleEnd();
    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      handleMove(e);
    };
    const handleTouchEnd = (e: TouchEvent) => {
      e.preventDefault();
      if (touchIdRef.current === null || 
          Array.from(e.changedTouches).some(t => t.identifier === touchIdRef.current)) {
        handleEnd();
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);
    window.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [isActive]);

  return (
    <div
      ref={containerRef}
      style={{
        position: "fixed",
        bottom: 30,
        left: 30,
        width: JOYSTICK_SIZE,
        height: JOYSTICK_SIZE,
        borderRadius: "50%",
        backgroundColor: "rgba(0, 0, 0, 0.3)",
        border: "3px solid rgba(255, 255, 255, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        touchAction: "none",
        userSelect: "none",
        zIndex: 100,
        pointerEvents: "auto",
      }}
      onMouseDown={handleStart}
      onTouchStart={handleStart}
    >
      <div
        ref={stickRef}
        style={{
          width: STICK_SIZE,
          height: STICK_SIZE,
          borderRadius: "50%",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          border: "2px solid rgba(0, 0, 0, 0.3)",
          transform: `translate(${position.x}px, ${position.y}px)`,
          transition: isActive ? "none" : "transform 0.2s ease-out",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

